import React, {useEffect, useState} from 'react';
import {
    Tabs,
    Tab,
    Box,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Divider,
    CircularProgress,
    Autocomplete,
    FormHelperText,
} from '@mui/material';
import {IUserData} from './type/IUserData';
import instance from '../../instance';
import CallNotification from '../../context/NotificationContext';

interface IProfilePageProps {
    setCurrentPageClass: React.Dispatch<React.SetStateAction<string>>;
    userId: number | null;
}

export default function ProfilePage({setCurrentPageClass, userId}: IProfilePageProps) {
    const {handleErrorNotification} = CallNotification();
    const [userInfo, setUserInfo] = useState<IUserData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [profileErrors, setProfileErrors] = useState<{[key: string]: string}>({});
    const [isProfileChanged, setIsProfileChanged] = useState(false);
    const [serviceCenterId, setServiceCenterId] = useState(undefined);

    const [profileForm, setProfileForm] = useState({
        name: '',
        address: '',
        e_mail: '',
        phone: '',
        flag_vat: false,
    });

    useEffect(() => {
        setCurrentPageClass('bg profile');
        if (userId) {
            void fetchUserInfo();
            void fetchServiceCenterId();
        }
    }, [userId]);

    useEffect(() => {
        if (!userInfo) return;

        const userFlagVat = userInfo.flag_vat === 'Y';
        const isChanged =
            profileForm.name !== (userInfo.name || '') ||
            profileForm.address !== (userInfo.address || '') ||
            profileForm.e_mail !== (userInfo.e_mail || '') ||
            profileForm.phone !== (userInfo.phone || '') ||
            profileForm.flag_vat !== userFlagVat;

        setIsProfileChanged(isChanged);
    }, [profileForm, userInfo]);

    const fetchServiceCenterId = async () => {
        try {
            const response = await instance.post('/LK', {
                method: 'CheckParentServiceCenter',
                params: { user_id: Number(userId) },
            });

            if (!response.data.error && response.data.result) {
                setServiceCenterId(response.data.result.parent_service_center_id);
            }
        } catch (error) {}
    };

    const fetchUserInfo = async () => {
        try {
            setIsLoading(true);
            const response = await instance.post('/LK', {
                method: 'GetMerchant',
                params: {user_id: userId},
            });

            const info = response.data.merchant;
            handleErrorNotification(response);
            setUserInfo(info);
            setProfileForm({
                name: info.name || '',
                address: info.address || '',
                e_mail: info.e_mail || '',
                phone: info.phone || '',
                flag_vat: info.flag_vat === 'Y',
            });
        } catch (error) {
            console.error('Failed to fetch user info', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleProfileChange = (field: string, value: any) => {
        setProfileForm(prev => {
            const updatedForm = {...prev, [field]: value};
            return updatedForm;
        });
    };

    const submitProfileUpdate = async () => {
        const errors: {[key: string]: string} = {};

        if (!profileForm.name) errors.name = 'Название организации обязательно';
        if (!profileForm.address) errors.address = 'Адрес обязателен';
        if (!profileForm.e_mail) errors.e_mail = 'Почта обязательна';
        if (!profileForm.phone) errors.phone = 'Телефон обязателен';

        if (Object.keys(errors).length > 0) {
            setProfileErrors(errors);
            return;
        }

        setProfileErrors({});
        setIsLoading(true);

        try {
            const response = await instance.post('/LK', {
                method: 'EditMerchant',
                params: {
                    user_id: userId,
                    ...profileForm,
                    flag_vat: profileForm.flag_vat ? 'Y' : 'N',
                    service_center_id: serviceCenterId,
                },
            });
            handleErrorNotification(response, true);
            setIsProfileChanged(false);
            void fetchUserInfo();
        } catch (error) {
            console.error('Failed to update profile', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box>
            <div className={'header'} style={{padding: '16px 0'}}>
                <h1
                    className={'title'}
                    style={{
                        margin: '0 0 0 1.5rem',
                        fontSize: '24px',
                        lineHeight: '29px',
                        fontWeight: 400,
                        color: '#fff',
                    }}
                >
                    Профиль
                </h1>
            </div>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                    <CircularProgress />
                </Box>
            ) : (
                <Box p={8} display="flex" justifyContent="center">
                    <Box display="flex" flexDirection="column" gap={2} width={600}>
                        <TextField
                            label="Название организации"
                            value={profileForm.name}
                            onChange={e => handleProfileChange('name', e.target.value)}
                            error={!!profileErrors.name}
                            helperText={profileErrors.name}
                            required
                        />
                        <TextField
                            label="Email"
                            value={profileForm.e_mail}
                            onChange={e => handleProfileChange('e_mail', e.target.value)}
                            error={!!profileErrors.e_mail}
                            helperText={profileErrors.e_mail}
                            required
                        />
                        <TextField
                            label="Телефон"
                            value={profileForm.phone}
                            onChange={e => handleProfileChange('phone', e.target.value)}
                            error={!!profileErrors.phone}
                            helperText={profileErrors.phone}
                            required
                        />
                        <TextField
                            label="Адрес"
                            value={profileForm.address}
                            onChange={e => handleProfileChange('address', e.target.value)}
                            error={!!profileErrors.address}
                            helperText={profileErrors.address}
                            required
                        />
                        <FormControlLabel
                            label={'Организация является плательщиком НДС'}
                            control={
                                <Checkbox
                                    checked={profileForm.flag_vat}
                                    onChange={e => handleProfileChange('flag_vat', e.target.checked)}
                                />
                            }
                        />
                        <Button
                            style={{marginTop: '20px'}}
                            variant="contained"
                            onClick={() => void submitProfileUpdate()}
                            disabled={!isProfileChanged || serviceCenterId === undefined}
                        >
                            Сохранить
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
